import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { getHeaders } from "../../helpers/helper";
import { getUserStoreIdFromLocalStorage } from "../../helpers/localStorageHelper";
import { OrderListDeliverySetting, OrderListResponse, OrderListSettingsResponse } from "../../models/orderListModels";
import axiosInstance from "../config/axiosInstance";
import { QueryKeys } from "../react-query/queryKeys";
import { OrderListDeliveryRequest } from "../../helpers/orderListDateHelper";

export const useOrderListSettings = () => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();
  return useQuery<OrderListSettingsResponse>({
    queryKey: [QueryKeys.ORDER_LIST_SETTINGS, storeId],
    queryFn: async () => {
      const headers = await getHeaders(getAccessTokenSilently);
      const { data } = await axiosInstance.get<OrderListSettingsResponse>("/OrderListSettings", { headers });
      return data;
    },
    enabled: !!getAccessTokenSilently && !!storeId,
  });
};

export const useSaveOrderListSettings = () => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();
  const queryClient = useQueryClient();
  return useMutation<OrderListSettingsResponse, unknown, { settings: OrderListDeliverySetting[]; deletedSettingIds: string[] }>({
    mutationFn: async ({ settings, deletedSettingIds }) => {
      const headers = await getHeaders(getAccessTokenSilently);
      const { data } = await axiosInstance.post<OrderListSettingsResponse>("/OrderListSettings", { settings, deletedSettingIds }, { headers });
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.ORDER_LIST_SETTINGS, storeId] });
    },
  });
};
export const useStoreBufferPreference = () => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();

  return useQuery<number>({
    queryKey: ["store-buffer-preference", storeId],
    queryFn: async () => {
      const headers = await getHeaders(getAccessTokenSilently);
      const { data } = await axiosInstance.get<{ bufferPercent: number }>("/StoreBufferPreference", { headers });
      return Number.isFinite(data?.bufferPercent) ? data.bufferPercent : 0;
    },
    enabled: !!getAccessTokenSilently && !!storeId,
    retry: 0,
    refetchOnWindowFocus: false,
  });
};

export const useSaveStoreBufferPreference = () => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();
  const queryClient = useQueryClient();

  return useMutation<number, unknown, number>({
    mutationFn: async (bufferPercent) => {
      const headers = await getHeaders(getAccessTokenSilently);
      const { data } = await axiosInstance.post<{ bufferPercent: number }>("/StoreBufferPreference", { bufferPercent }, { headers });
      return Number.isFinite(data?.bufferPercent) ? data.bufferPercent : 0;
    },
    onSuccess: async (savedBuffer) => {
      if (!storeId) {
        return;
      }

      await queryClient.setQueryData(["store-buffer-preference", storeId], savedBuffer);
      await queryClient.invalidateQueries({ queryKey: ["store-buffer-preference", storeId] });
    },
  });
};

export const useOrderListSections = (
  requests: OrderListDeliveryRequest<OrderListDeliverySetting>[],
  buffer: number,
  forceRefresh = false
) => {
  const { getAccessTokenSilently } = useAuth0();
  const storeId = getUserStoreIdFromLocalStorage();
  return useQueries({
    queries: requests.map(({ setting, deliveryDate }) => ({
      queryKey: [QueryKeys.ORDER_LIST, storeId, setting.id, deliveryDate, buffer, forceRefresh],
      queryFn: async () => {
        const headers = await getHeaders(getAccessTokenSilently);
        const { data } = await axiosInstance.get<OrderListResponse>("/OrderList", {
          headers,
          params: {
            deliverySettingId: setting.id,
            deliveryDate,
            overallBufferPercent: buffer,
            forceRefresh,
          },
        });
        return data;
      },
      enabled: !!getAccessTokenSilently && !!storeId && !!deliveryDate,
      retry: 0,
      refetchOnWindowFocus: false,
    })),
  });
};